// Client-side image compression — pure Canvas API, no dependencies.
//
// Why: large originals (Nikon Z mirrorless usually outputs 15–35 MB JPEGs)
// either hit our 12 MB backend cap, or get killed mid-upload by the ingress,
// surfacing as the dreaded axios "Network Error" toast. We compress on the
// client so uploads always go through.
//
// What gets compressed: JPEG, PNG, WebP, HEIC (when the browser can decode it).
// What doesn't: videos and anything we can't load into <img>. We just pass
// those through untouched.

const COMPRESSIBLE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

// Defaults tuned for premium-photography portfolio output.
//   - max edge 3000 px is well above what any web display needs (4K screens
//     top out at 3840 px wide, our display variant is downsized to 2400 px
//     server-side anyway). Lowering this from 4000 → 3000 cuts Canvas work
//     by ~44 % and the upload payload by ~30 %.
//   - quality 0.88 is visually indistinguishable from 0.92 for photos but
//     produces noticeably smaller files → faster upload.
//   - skipBelowBytes avoids re-encoding already-light files
const DEFAULTS = {
  maxEdge: 3000,
  quality: 0.88,
  outputType: "image/jpeg",
  skipBelowBytes: 3 * 1024 * 1024, // 3 MB
};

function fileFromBlob(blob, originalName) {
  // Re-encode keeps the same base name; the extension is forced to .jpg so
  // the backend's MIME map picks `image/jpeg` reliably.
  const base = (originalName || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, {
    type: blob.type || "image/jpeg",
    lastModified: Date.now(),
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Compress an image File if it's worth it. Returns the original File when:
 *   - the input isn't a known image MIME type
 *   - the input is already smaller than skipBelowBytes
 *   - decoding/encoding fails (defensive fallback so uploads never break)
 *
 * @param {File} file
 * @param {Object} [opts]
 * @returns {Promise<File>}
 */
export async function compressImageIfNeeded(file, opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };
  if (!file || !COMPRESSIBLE_TYPES.has(file.type)) return file;
  if (file.size <= cfg.skipBelowBytes) return file;

  try {
    const img = await loadImage(file);
    const { width: w0, height: h0 } = img;
    const longest = Math.max(w0, h0);
    const scale = longest > cfg.maxEdge ? cfg.maxEdge / longest : 1;
    const w = Math.round(w0 * scale);
    const h = Math.round(h0 * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, cfg.outputType, cfg.quality),
    );
    if (!blob) return file;

    // If compression somehow made the file bigger (rare, but happens with
    // tiny pristine JPEGs), keep the original.
    if (blob.size >= file.size) return file;
    return fileFromBlob(blob, file.name);
  } catch (err) {
    // Decoding failed (corrupted file, HEIC on a browser that can't render
    // it, etc.) → fall back to the original. Backend still has the 25 MB
    // hard cap to catch genuinely oversized files.
    if (typeof console !== "undefined") {
      console.warn("Image compression skipped:", err?.message || err);
    }
    return file;
  }
}
